import { useState, useEffect } from 'react';
import { IConfigBase, IConfigChild } from '../../ObjectTypes/SetupTypes';
import StoreX from '../../redux/oldStore';
import { ValueTruthy } from '../../utils/Tools';
import Editor from '../GenericForms/Editor';
import InputGradeRange from '../GenericForms/InputGradeRange';
import Icon, { IconType } from '../Icon/Icon';
import Modal from '../Modal/Modal';
import { EasyFileInput } from '../Tools/ExpandableDiv';
import { HtmlEditorPro2 } from '../Tools/htmlEditor2';
import ConfigFormEditor from './ConfigForm/ConfigFormEditor';
import ConfigFormLanding from './ConfigForm/ConfigFormLanding';
import { IConfigItemSummary } from './ConfigForm/ConfigFormTypes';
import ColorPicker from '../Tools/ColorPicker';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import Select from 'react-select';

interface ISetupConfigProps {
  valueUpdated: Function;
  valueFileUpdated?: Function;
  Config: IConfigBase;
  minimize?: boolean;
  isOwner?: boolean;
  hideHeader?: boolean;
  allConfigs?: IConfigChild[]; // Access to all configs for resolving RelatedConfigs
  isNested?: boolean;          // Flag to identify nested configs in modal
}

const SetupConfig = (props: ISetupConfigProps) => {
  const [startValue, setStartValue] = useState<any>();
  const [value, setValue] = useState<any>();
  const [showMore, setShowMore] = useState<boolean>();
  const [showEditor, setShowEditor] = useState<boolean>(false);
  const [enabled, setEnabled] = useState<boolean>(true);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<IConfigItemSummary>();
  const [showConfigEditorModal, setShowConfigEditorModal] = useState<boolean>(false);
  
  // New state for related configs functionality
  const [relatedConfigObjects, setRelatedConfigObjects] = useState<IConfigChild[]>([]);
  const [loadingRelatedConfigs, setLoadingRelatedConfigs] = useState<boolean>(false);
  const [relatedConfigsError, setRelatedConfigsError] = useState<string | null>(null);

  useEffect(() => {
    if (props.Config) {
      let tempValue: any = props.Config.Value;
      if (props.Config.ConfigType === 'bit-bool' || props.Config.ConfigType === 'bit-bool-not') {
        tempValue = tempValue?.toLowerCase() === 'true';
      }
      setValue(tempValue);
      setStartValue(tempValue);
      setEnabled(props.Config.UserRoleAccess?.toLowerCase() !== 'zfairs' || (props.isOwner ?? false));
    }
  }, [props.Config]);

  useEffect(() => {
    if (startValue === value) {
      return;
    }
    if (
      props.Config.ConfigType === 'bit-bool' ||
      props.Config.ConfigType === 'bit-bool-not' ||
      props.Config.ConfigType === 'bit-char' ||
      props.Config.ConfigType === 'bit-string' ||
      props.Config.DataType == 'bool' ||
      props.Config.DataType == 'html-full' ||
      props.Config.Key === 'Grade.Fair'
      //todo: why is this not always true??? html types what about just strings...
    ) {
      console.log('values changed');
      props.valueUpdated(props.Config.PublicId, value);
      setStartValue(value);
    }
  }, [value]);

  // New useEffect for resolving related configs
  useEffect(() => {
    if (props.Config && props.allConfigs && (props.Config as any).RelatedConfigs) {
      setLoadingRelatedConfigs(true);
      setRelatedConfigsError(null);
      
      const relatedIds = (props.Config as any).RelatedConfigs as string[];
      const resolvedConfigs = relatedIds
        .map(publicId => props.allConfigs?.find(config => config.PublicId === publicId))
        .filter(config => config !== undefined) as IConfigChild[];
      
      setRelatedConfigObjects(resolvedConfigs);
      setLoadingRelatedConfigs(false);
      
      // Set error if some configs couldn't be resolved
      if (resolvedConfigs.length !== relatedIds.length) {
        const missing = relatedIds.filter(id =>
          !props.allConfigs?.some(config => config.PublicId === id)
        );
        setRelatedConfigsError(`Could not find configs: ${missing.join(', ')}`);
      }
    } else {
      setRelatedConfigObjects([]);
      setLoadingRelatedConfigs(false);
      setRelatedConfigsError(null);
    }
  }, [props.Config, props.allConfigs]);

  const updateOnBlur = () => {
    if (startValue === value) return;
    if (props.Config.Required && !value) {
      setValue(startValue);
      return;
    }
    props.valueUpdated(props.Config.PublicId, value);
    setStartValue(value);
  };
  const updateOnBlur2 = (xValue: any) => {
    if (startValue === xValue) return;
    if (props.Config.Required && !xValue) {
      setValue(startValue);
      return;
    }
    props.valueUpdated(props.Config.PublicId, xValue);
    setStartValue(xValue);
  };
  const updateOnBlurColor = () => {
    if (startValue === value) {
      console.log('No change in value, not updating.');
      return;
    }
    if (props.Config.Required && !value) {
      console.log('Required field is empty, reverting to start value.');
      setValue(startValue);
      return;
    }
    props.valueUpdated(props.Config.PublicId, value);
    setStartValue(value);
  };

  const GetContestManagementType = () => {
    return (
      <>
        <div className="flex">
          <select
            id={props.Config.PublicId}
            value={value ?? '0'}
            className="form-control contest-mgt-type"
            disabled={!enabled}
            onChange={(e) => {
              setValue(e.target.value);
            }}>
            <option value={'0'}>Science Fair</option>
            <option value={'3'}>Invention Contest</option>
            <option value={'9'}>Film Festival</option>
            <option value={'2'}>Reflections</option>
            <option value={'7'}>Art Fair</option>
            <option value={'4'}>Seal of Biliteracy</option>
            <option value={'1'}>History Contest</option>
            <option value={'5'}>AcaDeca</option>
            <option value={'6'}>Spelling Bee</option>
            <option value={'8'}>Teacher Innovator Awards</option>
            <option value={'Z'}>Other</option>
          </select>
          <label className="flex" htmlFor={props.Config.PublicId}>
            {props.Config.Name} {zFairsOnlyLabel(props.Config)}
          </label>
        </div>
        {props.Config.Description && !props.isNested && (
          <span
            title="more info"
            className="click"
            onClick={() => {
              setShowMore(true);
            }}>
            <Icon type={IconType.info} />
          </span>
        )}
      </>
    );
  };

  const buildSetting = () => {
    if (props.Config.ConfigType === 'complex') {
      return GetComplexType(props.Config);
    }
    
    const baseElement = <div className="config-group-input">{buildSettingOuter()}</div>;
    
    // Add nested styling if this is a nested config
    if (props.isNested) {
      return (
        <div>
          {baseElement}
        </div>
      );
    }
    
    return baseElement;
  };

  const buildSettingOuter = () => {
    switch (props.Config.ConfigType) {
      case 'bit-bool-not':
      case 'bit-bool':
        return (
          <>
            <div>
              <label
                className={`config-bool id-${props.Config.PublicId}`}
                id={props.Config.PublicId}
                htmlFor={props.Config.PublicId}
                onClick={() => {
                  if (enabled) setValue(value ? false : true);
                }}>
                <div className="flex">
                  <div className="grow">
                    {props.Config.Name}
                    {props.Config.Required && <>*</>}
                    <div className="small">
                      {value ? <span className="label label-info">On</span> : <span className="label label-default">Off</span>}
                      {zFairsOnlyLabel(props.Config)}
                    </div>
                  </div>
                </div>
              </label>
            </div>
            <div className="flex">
              <label
                htmlFor={props.Config.PublicId}
                onClick={() => {
                  if (enabled) setValue(value ? false : true);
                }}>
                <Icon type={value ? IconType.on : IconType.off} addClass={`click config-switch ${value ? 'text-info' : ''}`} title="click to toggle on or off" />
              </label>
              {props.Config.Description && !props.isNested && (
                <span
                  title="more info"
                  className="click"
                  onClick={() => {
                    setShowMore(true);
                  }}>
                  <Icon type={IconType.info} />
                </span>
              )}
            </div>
          </>
        );
        break;
      case 'bit-char':
        return (
          <>
            <div>
              <label className={`config-bool id-${props.Config.PublicId}`} id={props.Config.PublicId} htmlFor={props.Config.PublicId}>
                <div className="flex">
                  <div className="grow">
                    {props.Config.Name}
                    {props.Config.Required && <>*</>}
                    <div className="small">
                      {value === '1' ? (
                        <span className="label label-info">Asked</span>
                      ) : value === '2' ? (
                        <span className="label label-warning">Reqired</span>
                      ) : (
                        <span className="label label-default">hidden</span>
                      )}
                      {zFairsOnlyLabel(props.Config)}
                    </div>
                  </div>
                </div>
              </label>
            </div>
            <div className="flex">
              <select
                id={props.Config.PublicId}
                value={value ?? '0'}
                className={'form-control hide-ask-require ' + ` config-hide-ask-require id-${props.Config.PublicId} ${props.Config.Required ? 'x-required' : ''}`}
                disabled={!enabled}
                onChange={(e) => {
                  setValue(e.target.value);
                }}>
                <option value={'0'}>Hidden</option>
                <option value={'1'}>Asked</option>
                <option value={'2'}>Required</option>
              </select>
              {props.Config.Description && !props.isNested && (
                <span
                  title="more info"
                  className="click"
                  onClick={() => {
                    setShowMore(true);
                  }}>
                  <Icon type={IconType.info} />
                </span>
              )}
            </div>
          </>
        );
        break;
      case 'fairSetting':
        return GetInputType(props.Config);
        break;
      // case "bit-string":
      //     return GetContestManagementType();
      //   break;
      case 'bit-string':
      case 'table-column':
      case 'session':
      case 'fairBigText':
        return GetInputType(props.Config);
        break;
      case 'complex':
        return GetComplexType(props.Config);
        break;
      case 'route-to':
        return GetRouteTo(props.Config);
        break;
      default:
        return (
          <>
            Unknown Config Type: {props.Config.ConfigType} {props.Config.DataType} {props.Config.Name} {props.Config.PublicId}
          </>
        );
        break;
    }
  };

  const maxLength = (maxLength: number | undefined | null, defaultNumber: number) => {
    if (maxLength === 0 || maxLength === undefined || maxLength === null || !maxLength || maxLength < 1) return defaultNumber;
    return maxLength;
  };
  const GetRouteTo = (config: IConfigBase) => {
    return (
      <a className="btn btn-default" href={StoreX.BuildUrlWithFair(config.RouteTo ?? '')} target={config.ToNewTab ? '_blank' : '_self'}>
        {config.ButtonName ?? config.RouteTo ?? 'Go to route missing...'}
      </a>
    );
  };

  const GetInputType = (config: IConfigBase) => {
    switch (config.DataType ?? 'text') {
      case 'string':
        return (
          <>
            <div>
              <label className={`id-${config.PublicId}`} htmlFor={config.PublicId}>
                <div className="flex">
                  <div className="grow">
                    {config.Name}
                    {config.Required && <>*</>}
                    <div className="small">{zFairsOnlyLabel(config)}</div>
                  </div>
                </div>
              </label>
            </div>
            <div className="flex">
              <input
                type="text"
                id={config.PublicId}
                className={'form-control' + ` config-text id-${config.PublicId} ${config.Required ? 'x-required' : ''}`}
                maxLength={maxLength(config.MaxLength, 250)}
                value={value ?? ''}
                onBlur={() => {
                  updateOnBlur();
                }}
                disabled={!enabled}
                onChange={(e) => {
                  setValue(e.target.value);
                }}
              />
              {props.Config.Description && !props.isNested && (
                <span
                  title="more info"
                  className="click"
                  onClick={() => {
                    setShowMore(true);
                  }}>
                  <Icon type={IconType.info} />
                </span>
              )}
            </div>
          </>
        );
        break;
      case 'password':
        return (
          <>
            <div>
              <label className={`id-${config.PublicId}`} htmlFor={config.PublicId}>
                <div className="flex">
                  <div className="grow">
                    {config.Name}
                    {config.Required && <>*</>}
                    <div className="small">{zFairsOnlyLabel(config)}</div>
                  </div>
                </div>
              </label>
            </div>
            <div className="flex">
              <input
                type="password"
                id={config.PublicId}
                className={'form-control' + ` config-pass id-${config.PublicId} ${config.Required ? 'x-required' : ''}`}
                maxLength={maxLength(config.MaxLength, 250)}
                value={value ?? ''}
                onBlur={() => {
                  updateOnBlur();
                }}
                disabled={!enabled}
                onChange={(e) => {
                  setValue(e.target.value);
                }}
              />
              {props.Config.Description && !props.isNested && (
                <span
                  title="more info"
                  className="click"
                  onClick={() => {
                    setShowMore(true);
                  }}>
                  <Icon type={IconType.info} />
                </span>
              )}
            </div>
          </>
        );
        break;
      case 'number':
        return (
          <>
            <div>
              <label className={`id-${config.PublicId}`} htmlFor={config.PublicId}>
                <div className="flex">
                  <div className="grow">
                    {config.Name}
                    {config.Required && <>*</>}
                    <div className="small">{zFairsOnlyLabel(config)}</div>
                  </div>
                </div>
              </label>
            </div>
            <div className="flex">
              <input
                type="number"
                id={config.PublicId}
                className={'form-control' + ` config-pass id-${config.PublicId} ${config.Required ? 'x-required' : ''}`}
                maxLength={maxLength(config.MaxLength, 250)}
                value={value ?? ''}
                onBlur={() => {
                  updateOnBlur();
                }}
                disabled={!enabled}
                onChange={(e) => {
                  setValue(e.target.value);
                }}
              />
              {props.Config.Description && !props.isNested && (
                <span
                  title="more info"
                  className="click"
                  onClick={() => {
                    setShowMore(true);
                  }}>
                  <Icon type={IconType.info} />
                </span>
              )}
            </div>
          </>
        );
        break;
      case 'dropdown-multi': {
        const parseIds = (raw: any): string[] => {
          if (raw == null || raw === '') return [];
          if (Array.isArray(raw)) return raw.map(String);
          const s = String(raw).trim();
          if (!s) return [];
          let body = s;
          if (body[0] === '[') {
            try {
              const arr = JSON.parse(body);
              return Array.isArray(arr) ? arr.map(String) : [];
            } catch {
              body = body.replace(/^\[/, '').replace(/\]$/, '');
            }
          }
          return body
            .split(/[,|;]/)
            .map((x) => x.trim().replace(/^"|"$/g, ''))
            .filter(Boolean);
        };
        const selected = parseIds(value);
        const options = (config.ValueOptions ?? []).map((x) => ({ label: x.Name, value: String(x.Value) }));
        return (
          <>
            <div>
              <label className={`id-${config.PublicId}`} htmlFor={config.PublicId}>
                <div className="flex">
                  <div className="grow">
                    {config.Name}
                    {config.Required && <>*</>}
                    <div className="small">{zFairsOnlyLabel(config)}</div>
                  </div>
                </div>
              </label>
            </div>
            <div className="flex">
              <div
                data-testid={`setup-config-multi-${config.Key}`}
                className={`grow config-dropdown-multi id-${config.PublicId} ${config.Required ? 'x-required' : ''}`}>
                <Select
                  inputId={config.PublicId}
                  isMulti
                  isDisabled={!enabled}
                  options={options}
                  placeholder="All (no limit)"
                  value={options.filter((o) => selected.includes(o.value))}
                  onChange={(e) => {
                    const ids = (e ?? []).map((x) => x.value);
                    const serialized = JSON.stringify(ids.map((x) => +x).filter((n) => n > 0));
                    setValue(serialized);
                    updateOnBlur2(serialized);
                  }}
                />
              </div>
              {props.Config.Description && !props.isNested && (
                <span
                  title="more info"
                  className="click"
                  onClick={() => {
                    setShowMore(true);
                  }}>
                  <Icon type={IconType.info} />
                </span>
              )}
            </div>
          </>
        );
        break;
        }
      case 'dropdown':
        return (
          <>
            <div>
              <label className={`id-${config.PublicId}`} htmlFor={config.PublicId}>
                <div className="flex">
                  <div className="grow">
                    {config.Name}
                    {config.Required && <>*</>}
                    <div className="small">{zFairsOnlyLabel(config)}</div>
                  </div>
                </div>
              </label>
            </div>
            <div className="flex">
              <select
                id={config.PublicId}
                className={`form-control config-dropdown id-${props.Config.PublicId} ${props.Config.Required ? 'x-required' : ''}`}
                value={value}
                disabled={!enabled}
                onChange={(e) => {
                  setValue(e.target.value);
                  updateOnBlur2(e.target.value);
                }}>
                <option value={''}>select</option>
                {props.Config.ValueOptions?.map((x, i) => {
                  return (
                    <option key={`id-${props.Config.PublicId}-${i}`} value={x.Value}>
                      {x.Name}
                    </option>
                  );
                })}
              </select>
              {props.Config.Description && !props.isNested && (
                <span
                  title="more info"
                  className="click"
                  onClick={() => {
                    setShowMore(true);
                  }}>
                  <Icon type={IconType.info} />
                </span>
              )}
            </div>
          </>
        );
        break;
      case 'dateTime':
        return (
          <>
            <div>
              <label className={`id-${config.PublicId}`} htmlFor={config.PublicId}>
                <div className="flex">
                  <div className="grow">
                    {config.Name}
                    {config.Required && <>*</>}
                    <div className="small">{zFairsOnlyLabel(config)}</div>
                  </div>
                </div>
              </label>
            </div>
            <div className="flex">
              <input
                type="datetime-local"
                id={config.PublicId}
                className={'form-control' + ` config-pass id-${config.PublicId} ${config.Required ? 'x-required' : ''}`}
                maxLength={maxLength(config.MaxLength, 250)}
                value={value ?? ''}
                onBlur={() => {
                  updateOnBlur();
                }}
                disabled={!enabled}
                onChange={(e) => {
                  setValue(e.target.value);
                }}
              />
              {props.Config.Description && !props.isNested && (
                <span
                  title="more info"
                  className="click"
                  onClick={() => {
                    setShowMore(true);
                  }}>
                  <Icon type={IconType.info} />
                </span>
              )}
            </div>
          </>
        );
        break;
      case 'date':
        return (
          <>
            <div>
              <label className={`id-${config.PublicId}`} htmlFor={config.PublicId}>
                <div className="flex">
                  <div className="grow">
                    {config.Name}
                    {config.Required && <>*</>}
                    <div className="small">{zFairsOnlyLabel(config)}</div>
                  </div>
                </div>
              </label>
            </div>
            <div className="flex">
              <input
                type="date"
                id={config.PublicId}
                className={'form-control' + ` config-pass id-${config.PublicId} ${config.Required ? 'x-required' : ''}`}
                maxLength={maxLength(config.MaxLength, 250)}
                value={value ?? ''}
                onBlur={() => {
                  updateOnBlur();
                }}
                disabled={!enabled}
                onChange={(e) => {
                  setValue(e.target.value);
                }}
              />
              {config.Description && !props.isNested && (
                <span
                  title="more info"
                  className="click"
                  onClick={() => {
                    setShowMore(true);
                  }}>
                  <Icon type={IconType.info} />
                </span>
              )}
            </div>
          </>
        );
        break;
      case 'time':
        return (
          <>
            <div>
              <label className={`id-${config.PublicId}`} htmlFor={config.PublicId}>
                <div className="flex">
                  <div className="grow">
                    {config.Name}
                    {config.Required && <>*</>}
                    <div className="small">{zFairsOnlyLabel(config)}</div>
                  </div>
                </div>
              </label>
            </div>
            <div className="flex">
              <input
                type="time"
                id={config.PublicId}
                className={'form-control' + ` config-pass id-${config.PublicId} ${config.Required ? 'x-required' : ''}`}
                maxLength={maxLength(config.MaxLength, 250)}
                value={value ?? ''}
                onBlur={() => {
                  updateOnBlur();
                }}
                disabled={!enabled}
                onChange={(e) => {
                  setValue(e.target.value);
                }}
              />
              {props.Config.Description && !props.isNested && (
                <span
                  title="more info"
                  className="click"
                  onClick={() => {
                    setShowMore(true);
                  }}>
                  <Icon type={IconType.info} />
                </span>
              )}
            </div>
          </>
        );
        break;
      case 'bool':
        return (
          <>
            <div>
              <label
                className={`config-bool id-${props.Config.PublicId}`}
                id={props.Config.PublicId}
                htmlFor={props.Config.PublicId}
                onClick={() => {
                  if (enabled) setValue(ValueTruthy(value) ? false : true);
                }}>
                <div className="flex">
                  <div className="grow">
                    {props.Config.Name}
                    {props.Config.Required && <>*</>}
                    <div className="small">
                      {ValueTruthy(value) ? <span className="label label-info">On</span> : <span className="label label-default">Off</span>}
                      {zFairsOnlyLabel(props.Config)}
                    </div>
                  </div>
                </div>
              </label>
            </div>
            <div className='flex'>
              <label
                htmlFor={props.Config.PublicId}
                onClick={() => {
                  if (enabled) setValue(ValueTruthy(value) ? false : true);
                }}>
                <Icon type={value ? IconType.on : IconType.off} addClass={`click config-switch ${value ? 'text-info' : ''}`} title="click to toggle on or off" />
              </label>
              {props.Config.Description && !props.isNested && (
                <span
                  title="more info"
                  className="click"
                  onClick={() => {
                    setShowMore(true);
                  }}>
                  <Icon type={IconType.info} />
                </span>
              )}
            </div>
          </>
        );
        break;
      case 'html-full':
        return (
          <>
            <div>
              <label className={`id-${config.PublicId}`} htmlFor={config.PublicId}>
                <div className="flex">
                  <div className="grow">
                    {config.Name}
                    {config.Required && <>*</>}
                    <div className="small">{zFairsOnlyLabel(config)}</div>
                  </div>
                </div>
              </label>
            </div>
            <div className="flex">
              <HtmlEditorPro2
                value={value}
                changed={(x) => {
                  setValue(x);
                }}
                id={config.PublicId}
                btnClass={`btn-xs config-html-full id-${props.Config.PublicId} ${props.Config.Required ? 'x-required' : ''}`}
              />
              {props.Config.Description && !props.isNested && (
                <span
                  title="more info"
                  className="click"
                  onClick={() => {
                    setShowMore(true);
                  }}>
                  <Icon type={IconType.info} />
                </span>
              )}
            </div>
          </>
        );
        break;
      case 'html':
      case 'textarea':
        return (
          <>
            <div>
              <label className={`id-${config.PublicId}`} htmlFor={config.PublicId}>
                <div className="flex">
                  <div className="grow">
                    {config.Name}
                    {config.Required && <>*</>}
                    <div className="small">{zFairsOnlyLabel(config)}</div>
                  </div>
                </div>
              </label>
            </div>
            <div className="flex">
              <button
                type="button"
                className={`btn btn-secondary btn-xs config-html id-${props.Config.PublicId} ${props.Config.Required ? 'x-required' : ''}`}
                disabled={!enabled}
                onClick={() => {
                  if (enabled) setShowEditor(true);
                }}>
                <Icon type={IconType.edit} /> Edit
              </button>
              {props.Config.Description && !props.isNested && (
                <span
                  title="more info"
                  className="click"
                  onClick={() => {
                    setShowMore(true);
                  }}>
                  <Icon type={IconType.info} />
                </span>
              )}
            </div>
          </>
        );
        break;
      case 'custom':
        if (props.Config.Key === 'Grade.Fair') {
          return (
            <>
              <div>
                <label className={`id-${config.PublicId}`} htmlFor={config.PublicId}>
                  <div className="flex">
                    <div className="grow">
                      {config.Name}
                      {config.Required && <>*</>}
                      <div className="small">{zFairsOnlyLabel(config)}</div>
                    </div>
                  </div>
                </label>
              </div>
              <div className="flex">
                <InputGradeRange
                  key={props.Config.PublicId}
                  forLow={config.PublicId}
                  type={'full-pipes'}
                  onBlur={() => {
                    updateOnBlur();
                  }}
                  readOnly={!enabled}
                  onValueChange={(v) => {
                    setValue(v + '');
                  }}
                  value={value}
                  delimiter={'|'}
                  clickToEdit={false}
                />
                {props.Config.Description && !props.isNested && (
                  <span
                    title="more info"
                    className="click"
                    onClick={() => {
                      setShowMore(true);
                    }}>
                    <Icon type={IconType.info} />
                  </span>
                )}
              </div>
            </>
          );
        }
        return (
          <>
            <div>
              <label className={`id-${config.PublicId}`} htmlFor={config.PublicId}>
                <div className="flex">
                  <div className="grow">
                    {config.Name}
                    {config.Required && <>*</>}
                    <div className="small">{zFairsOnlyLabel(config)}</div>
                  </div>
                </div>
              </label>
            </div>
            <div className="flex">
              <button
                type="button"
                className={`btn btn-secondary btn-xs config-html id-${props.Config.PublicId} ${props.Config.Required ? 'x-required' : ''}`}
                disabled={!enabled}
                onClick={() => {
                  if (enabled) {
                    let c = props.Config;
                    setSelectedItem({
                      Name: props.Config.Name,
                      Id: c.Key,
                      Type: c.Key,
                    });
                    setShowEditModal(true);
                  }
                }}>
                <Icon type={IconType.edit} /> Edit
              </button>
              {props.Config.Description && !props.isNested && (
                <span
                  title="more info"
                  className="click"
                  onClick={() => {
                    setShowMore(true);
                  }}>
                  <Icon type={IconType.info} />
                </span>
              )}
            </div>
          </>
        );
        break;
      case 'file-path-img':
        return (
          <>
            <div>
              <label className={`id-${config.PublicId}`} htmlFor={config.PublicId}>
                <div className="flex">
                  <div className="grow">
                    {config.Name}
                    {config.Required && <>*</>}
                    <div className="small">
                      {value ? <span className="label label-info">On</span> : <span className="label label-default">Off</span>}
                      {zFairsOnlyLabel(config)}
                    </div>
                  </div>
                </div>
              </label>
            </div>
            <div className="flex">
              <EasyFileInput
                htmlId={config.PublicId}
                exts={'.jpg,.jpeg,.png,.gif,.webp,.heic'}
                value={value}
                onSelectFile={(f) => {
                  console.log('File Changed: onSelectFile');
                  fileChange(f);
                }}
                onSelect={(v) => {
                  console.log('File Changed: onSelect');
                  fileChange(undefined, v);
                }}
                valueOptions={config.ValueOptions}
              />
              {props.Config.Description && !props.isNested && (
                <span
                  title="more info"
                  className="click"
                  onClick={() => {
                    setShowMore(true);
                  }}>
                  <Icon type={IconType.info} />
                </span>
              )}
            </div>
          </>
        );
        break;
      case 'color':
        return (
          <>
            <div>
              <label className={`id-${config.PublicId}`} htmlFor={config.PublicId}>
                <div className="flex">
                  <div className="grow">
                    {config.Name}
                    {config.Required && <>*</>}
                    <div className="small">
                      {value ? <span className="label label-info">On</span> : <span className="label label-default">Off</span>}
                      {zFairsOnlyLabel(config)}
                    </div>
                  </div>
                </div>
              </label>
            </div>
            <div className="flex">
              <ColorPicker
                value={value}
                onChange={(v) => {
                  setValue(v);
                }}
                className={'config-color-picker' + ` id-${props.Config.PublicId} ${props.Config.Required ? 'x-required' : ''}`}
                disabled={!enabled}
                onBlur={() => {
                  updateOnBlurColor();
                }}
              />
              {props.Config.Description && !props.isNested && (
                <span
                  title="more info"
                  className="click"
                  onClick={() => {
                    setShowMore(true);
                  }}>
                  <Icon type={IconType.info} />
                </span>
              )}
            </div>
          </>
        );
        break;
      default:
        return (
          <>
            Unknown Input Type: {props.Config.ConfigType} ({props.Config.DataType}) {props.Config.Name} {props.Config.PublicId}
          </>
        );
        break;
    }
  };

  const GetComplexType = (config: IConfigBase) => {
    //TODO check if there is a modal to open...
    //Else
    if (config.ComplexKey && config.ComplexKey.toLowerCase().indexOf('modal:') > -1) {
      return (
        <div className={'config-group-input' + ` config-complex id-${props.Config.PublicId} ${props.Config.Required ? 'x-required' : ''}`}>
          <div>
            <label className={`id-${config.PublicId}`} htmlFor={config.PublicId}>
              <div className="flex">
                <div className="grow">
                  {config.Name}
                  {config.Required && <>*</>}
                  <div className="small">{zFairsOnlyLabel(config)}</div>
                </div>
              </div>
            </label>
          </div>
          <div className="flex">
            <button
              type="button"
              onClick={() => {
                setShowConfigEditorModal(true);
              }}
              className="btn btn-secondary btn-xs complex-btn"
              title={`Open ${config.Name} to manage it.`}>
              <Icon type={IconType.configure} /> Edit
            </button>
            {config.Description && !props.isNested && (
              <span
                title="more info"
                className="click"
                onClick={() => {
                  setShowMore(true);
                }}>
                <Icon type={IconType.info} />
              </span>
            )}
          </div>
        </div>
      );
    } else if (config.ComplexKey && config.ComplexKey.toLowerCase().indexOf('header') > -1) {
      if (props.hideHeader) return null;

      return (
        <div className={'config-group-input-header' + ` config-custom id-${props.Config.PublicId} ${props.Config.Required ? 'x-required' : ''}`}>
          <h4>{config.Name}</h4>
          {config.Description && <div dangerouslySetInnerHTML={{ __html: config.Description }}></div>}
        </div>
      );
    } else if (config.ComplexKey && config.ComplexKey.toLowerCase().indexOf('inline') > -1) {
      let values = config.ComplexKey.toLowerCase().split(':');
      let close = values[0].indexOf('close') > -1 || props.minimize;
      return (
        <div className={'config-group-input-inline' + ` config-custom id-${props.Config.PublicId} ${props.Config.Required ? 'x-required' : ''}`}>
          {config.Description && <div dangerouslySetInnerHTML={{ __html: config.Description }}></div>}
          <ConfigFormLanding type={values[1]} expandable={true} startClosed={close} />
        </div>
      );
    } else if (config.ComplexKey)
      return (
        <div className={'config-group-input' + ` config-complex id-${props.Config.PublicId} ${props.Config.Required ? 'x-required' : ''}`}>
          <a href={StoreX.BuildUrlWithFair(config.ComplexKey)} className="btn btn-secondary complex-btn" target="_blank" title={`Open ${config.Name} in a new tab.`}>
            <Icon type={IconType.configure} /> {config.Name}
          </a>
        </div>
      );

    return <div className={'config-group-input' + ` config-complex id-${props.Config.PublicId} ${props.Config.Required ? 'x-required' : ''}`}>Unknow complex type... </div>;
  };
  const zFairsOnlyLabel = (config: IConfigBase) => {
    if (props.Config.UserRoleAccess?.toLowerCase() === 'zfairs') {
      return <span className="label label-warning bummper-left">zFairs Only</span>;
    }
    return <></>;
  };

  const fileChange = (file?: File, v?: any) => {
    if (props.valueFileUpdated) {
      let newValue = file?.name ?? v; // ?? value;
      console.log(newValue, props.Config.PublicId);
      console.log(file);
      props.valueFileUpdated(props.Config.PublicId, newValue, file);
      setStartValue(newValue);
      setValue(newValue);
    } else {
      console.log('valueFileUpdated not set!');
    }
  };

  return (
    <>
      {props.Config && (
        <>
          <div className="config-item" id={`config:${props.Config.PublicId}`}>
            {buildSetting()}
          </div>
        </>
      )}
      {showMore && (
        <Modal setModalOpen={setShowMore} title={`More Info`} className={``} size="xl">
          {/* <h4>{props.Config.Name}</h4> */}

          {(() => {
            // Type assertion to access the new fields
            const configChild = props.Config as any;
            const hasRelatedConfigs = configChild.RelatedConfigs && configChild.RelatedConfigs.length > 0;

            return (
              <>
                <Tabs>
                  <TabList>
                    <Tab>Base</Tab>
                    {hasRelatedConfigs && <Tab>Related Configs ({configChild.RelatedConfigs.length})</Tab>}
                  </TabList>
                  <TabPanel>
                          
                            <SetupConfig
                              Config={props.Config}
                              valueUpdated={props.valueUpdated}
                              valueFileUpdated={props.valueFileUpdated}
                              minimize={false}
                              isOwner={props.isOwner}
                              hideHeader={false}
                              allConfigs={props.allConfigs}
                              isNested={true}
                            />
                    {/* Description Section */}
                    {props.Config.Description && (
                      <div style={{ marginBottom: '20px' }}>
                        <h5>Description</h5>
                        <div dangerouslySetInnerHTML={{ __html: props.Config.Description }}></div>
                      </div>
                    )}

                    {/* Client Tags Section */}
                    {configChild.ClientTags && configChild.ClientTags.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <h5>Tags</h5>
                        <div>
                          {configChild.ClientTags.map((tag: string, index: number) => (
                            <span
                              key={index}
                              className="label label-info"
                              style={{
                                display: 'inline-block',
                                margin: '2px 4px 2px 0',
                                padding: '4px 8px',
                                backgroundColor: '#5bc0de',
                                color: 'white',
                                borderRadius: '3px',
                                fontSize: '12px',
                              }}>
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Prerequisites Section */}
                    {configChild.PreReqText && (
                      <div style={{ marginBottom: '20px' }}>
                        <h5>Prerequisites</h5>
                        <div
                          style={{
                            padding: '10px',
                            backgroundColor: '#fcf8e3',
                            border: '1px solid #faebcc',
                            borderRadius: '4px',
                            color: '#8a6d3b',
                          }}>
                          {configChild.PreReqText}
                        </div>
                      </div>
                    )}
                  </TabPanel>
                  <TabPanel>
                    <h5>Related Configurations</h5>
                    {loadingRelatedConfigs ? (
                      <div style={{ padding: '20px', textAlign: 'center' }}>
                        <div className="spinner-border spinner-border-sm" role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                        <div style={{ marginTop: '10px' }}>Loading related configurations...</div>
                      </div>
                    )  : (
                      <div>
                        {relatedConfigObjects.map((relatedConfig, index) => (
                          <div>                            
                            <SetupConfig
                              Config={relatedConfig}
                              valueUpdated={props.valueUpdated}
                              valueFileUpdated={props.valueFileUpdated}
                              minimize={false}
                              isOwner={props.isOwner}
                              hideHeader={false}
                              allConfigs={props.allConfigs}
                              isNested={true}
                            />
                          </div>
                        ))}
                        {relatedConfigsError && <div className="alert alert-warning" style={{ marginTop: '15px' }}>
                        <strong>Warning:</strong> {relatedConfigsError}
                      </div>}
                        {relatedConfigObjects.length === 0 && (
                          <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#666',
                            fontStyle: 'italic'
                          }}>
                            No related configurations found.
                          </div>
                        )}
                      </div>
                    )}
                  </TabPanel>
                </Tabs>
              </>
            );
          })()}
        </Modal>
      )}
      {showEditor && (
        <Modal
          setModalOpen={() => {
            if (props.Config.DataType === 'html') {
              //updateOnBlur2(value);
            } else {
              updateOnBlur();
            }
            setShowEditor(false);
          }}
          title={`Editor`}
          className={``}
          size="xl"
          // noCloseOnBackgroundClick
        >
          <h4>{props.Config.Name}</h4>
          <div>
            {props.Config.DataType === 'html' ? (
              <Editor
                height="normal"
                value={value ?? ''}
                options={'full'}
                changed={(x) => {
                  setValue(x);
                }}
                onblur={(value) => {
                  updateOnBlur2(value);
                }}
                readOnly={false}
                saveBeforeChange={true}
                close={() => {
                  setShowEditor(false);
                }}
                hideExpand={true}
              />
            ) : (
              <>
                <textarea
                  id={props.Config.PublicId}
                  className="form-control"
                  maxLength={maxLength(props.Config.MaxLength, 250)}
                  value={value ?? ''}
                  onBlur={() => {
                    updateOnBlur();
                  }}
                  disabled={!enabled}
                  onChange={(e) => {
                    setValue(e.target.value);
                  }}
                  rows={15}></textarea>
              </>
            )}
          </div>
        </Modal>
      )}

      {selectedItem && showEditModal && (
        <ConfigFormEditor
          type={selectedItem.Type ?? props.Config.Key ?? 'oops'}
          item={selectedItem}
          canSave={true}
          noEdit={false}
          onClose={() => {
            setShowEditModal(false);
          }}
          onSave={() => {
            setSelectedItem(undefined);
            setShowEditModal(false);
          }}
          onRemove={() => {
            setSelectedItem(undefined);
            setShowEditModal(false);
          }}
        />
      )}

      {showConfigEditorModal && (
        <Modal setModalOpen={setShowConfigEditorModal} title={props.Config.Name} className={``} size="xl">
          <ConfigFormLanding type={props.Config.ComplexKey?.substring(6) ?? 'oops'} />
        </Modal>
      )}
    </>
  );
};

export default SetupConfig;
